'use client';

import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Compass } from 'lucide-react';
import { toast } from 'sonner';
import FeedCard from './FeedCard';
import FeedCommunitySelect from '@/components/FeedCommunitySelect';
import EmptyState from '@/components/ui/EmptyState';
import { getOrCreateVisitorId } from '@/lib/visitorId';
import {
  fetchDiscoverTopics,
  followTopicApi,
  unfollowTopicApi,
  type DiscoverTopicDTO,
} from '@/lib/discoverApi';
import { useJoinGroupFromInvite } from '@/hooks/useJoinGroupFromInvite';

type CommunityOption = { id: string; name: string };

export type DiscoverFeed = {
  id: string;
  title: string;
  description: string;
  descriptionHtml?: string;
  category: string;
  groupId: string;
  groupName: string;
  followerCount: number;
  following: boolean;
  isGroupMember: boolean;
  feedIcon: string;
  feedColor: string;
  groupIconUrl?: string | null;
};

function mapDto(d: DiscoverTopicDTO): DiscoverFeed {
  return { ...d };
}

function firstGroupIdForFollowedTopic(feeds: DiscoverFeed[]): string | null {
  for (const f of feeds) {
    if (!f.following) continue;
    const gid = f.groupId?.trim();
    if (gid) return gid;
  }
  return null;
}

export default function FeedDiscoveryContent() {
  const [visitorId, setVisitorId] = useState('');
  const [feeds, setFeeds] = useState<DiscoverFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeGroupId, setActiveGroupId] = useState<string>('');
  /** Dès qu’on touche le filtre (sélecteur ou bouton), on ne re-synchronise plus jusqu’au prochain F5. */
  const userEditedFilter = useRef(false);

  useLayoutEffect(() => {
    setVisitorId(getOrCreateVisitorId());
  }, []);

  const loadFeeds = useCallback(async () => {
    if (!visitorId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const items = await fetchDiscoverTopics(visitorId);
      setFeeds(items.map(mapDto));
    } catch {
      toast.error('Impossible de charger les rubriques pour le moment.');
      setFeeds([]);
    } finally {
      setLoading(false);
    }
  }, [visitorId]);

  useEffect(() => {
    void loadFeeds();
  }, [loadFeeds]);

  useJoinGroupFromInvite({
    visitorId,
    cleanPath: '/feed-discovery',
    successMessage:
      'Communauté rejointe ! Vous suivez maintenant les rubriques de la communauté (vous pouvez retirer des abonnements dans Mon compte).',
    onSuccess: () => loadFeeds(),
  });

  const communityOptions: CommunityOption[] = useMemo(() => {
    const byId = new Map<string, string>();
    feeds.forEach((f) => {
      if (!f.isGroupMember) return;
      const gid = f.groupId?.trim();
      if (gid && !byId.has(gid)) {
        byId.set(gid, f.groupName?.trim() || 'Communauté');
      }
    });
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [feeds]);

  const discoverTabs = useMemo(
    () => communityOptions.map((c) => ({ id: c.id, label: c.name })),
    [communityOptions]
  );

  const topicCounts = useMemo(() => {
    const c: Record<string, number> = {};
    feeds.forEach((f) => {
      const gid = f.groupId?.trim();
      if (gid) c[gid] = (c[gid] || 0) + 1;
    });
    return c;
  }, [feeds]);

  useEffect(() => {
    const ids = new Set(discoverTabs.map((t) => t.id));
    if (!ids.has(activeGroupId)) setActiveGroupId(discoverTabs[0]?.id ?? '');
  }, [discoverTabs, activeGroupId]);

  const onCommunityFilterChange = useCallback((id: string) => {
    userEditedFilter.current = true;
    setActiveGroupId(id);
  }, []);

  /** Après chaque chargement (et au refresh) : 1ʳᵉ commu des rubriques suivies, sauf si l’utilisateur a changé le filtre dans la session. */
  useEffect(() => {
    if (!visitorId || loading || userEditedFilter.current) return;
    const preferred = firstGroupIdForFollowedTopic(feeds);
    if (preferred) {
      const tabIds = new Set(communityOptions.map((c) => c.id));
      if (tabIds.has(preferred)) {
        setActiveGroupId(preferred);
        return;
      }
    }
    setActiveGroupId(communityOptions[0]?.id ?? '');
  }, [visitorId, loading, feeds, communityOptions]);

  const filtered = useMemo(() => {
    if (!activeGroupId) return [];
    return feeds.filter((f) => f.groupId === activeGroupId);
  }, [feeds, activeGroupId]);

  const handleFeedAction = async (feed: DiscoverFeed) => {
    if (!visitorId) {
      toast.error('Session introuvable. Rafraîchis la page.');
      return;
    }
    try {
      if (feed.following) {
        await unfollowTopicApi(visitorId, feed.id);
        toast.info(`Vous ne suivez plus « ${feed.title} »`);
      } else {
        if (!feed.isGroupMember) return;
        await followTopicApi(visitorId, feed.id);
        toast.success(`Vous suivez maintenant « ${feed.title} »`);
      }
      await loadFeeds();
    } catch {
      toast.error('Action impossible. Réessaie dans un instant.');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1E1B4B]">Toutes les rubriques</h1>
          </div>
        </div>
        {!loading && feeds.length > 0 && (
          <div className="mt-2.5 w-full min-w-0 sm:mt-3">
            <FeedCommunitySelect
              tabs={discoverTabs}
              value={activeGroupId}
              onChange={onCommunityFilterChange}
              topicCounts={topicCounts}
            />
          </div>
        )}
      </div>

      <section>
        {loading ? (
          <div className="text-center py-16 text-sm text-[#333483]">Chargement des rubriques…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Compass}
            title="Aucune rubrique ne correspond"
            description="Aucune rubrique dans cette communauté."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
            {filtered.map((feed) => (
              <FeedCard key={feed.id} feed={feed} onAction={handleFeedAction} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
